import { useState, useEffect } from 'react'
import {
  Search, Edit2, Check, X, AlertTriangle,
  Layers, BookOpen, Scissors, Package, RefreshCw
} from 'lucide-react'
import { spkService } from '../services/spkService'

const STATION_TYPES = [
  { id: 'susun', label: 'QC Susun', icon: Layers, color: 'amber', description: 'Edit data QC dari proses susun halaman' },
  { id: 'binding', label: 'QC Binding', icon: BookOpen, color: 'emerald', description: 'Edit data QC dari proses binding' },
  { id: 'potong', label: 'QC Potong Sisir', icon: Scissors, color: 'cyan', description: 'Edit data QC dari proses potong sisir' },
  { id: 'laminasi', label: 'QC Laminasi', icon: Package, color: 'pink', description: 'Edit data QC dari proses laminasi cover' },
]

function EditQCHistoryPage() {
  const [selectedStation, setSelectedStation] = useState(null)
  const [spks, setSpks] = useState([])
  const [loading, setLoading] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [editingItem, setEditingItem] = useState(null)

  useEffect(() => {
    if (selectedStation) {
      fetchData()
    }
  }, [selectedStation])

  const fetchData = async () => {
    setLoading(true)
    try {
      const res = await spkService.getAll({ limit: 500, page: 1 })
      if (res.success) {
        setSpks(res.data.spks || [])
      }
    } catch (error) {
      console.error('Error fetching data:', error)
    } finally {
      setLoading(false)
    }
  }

  const getQCDataForStation = () => {
    const results = []

    spks.forEach(spk => {
      const batches = spk.batches || []

      if (selectedStation === 'laminasi') {
        // Laminasi QC is stored in spk.qc_data
        const laminasiData = spk.qc_data?.laminasi
        if (laminasiData) {
          results.push({
            type: 'spk',
            spk,
            spkId: spk.id,
            qcType: 'laminasi',
            schoolCode: null,
            data: laminasiData,
            label: `${spk.judul} - Cover Reguler`
          })
        }
        // Also check custom school laminasi
        const customSchools = spk.customSchools || []
        customSchools.forEach(cs => {
          const csLaminasi = spk.qc_data?.[`laminasi_${cs.school_code}`]
          if (csLaminasi) {
            results.push({
              type: 'spk',
              spk,
              spkId: spk.id,
              qcType: 'laminasi',
              schoolCode: cs.school_code,
              data: csLaminasi,
              label: `${spk.judul} - Cover ${cs.school_code}`
            })
          }
        })
      } else {
        // Susun, Binding, Potong are stored in batch.qc_data
        batches.forEach(batch => {
          const qcData = batch.qc_data?.[selectedStation]
          if (qcData) {
            results.push({
              type: 'batch',
              spk,
              spkId: spk.id,
              batch,
              batchId: batch.id,
              qcType: selectedStation,
              data: qcData,
              label: `${spk.judul} - ${batch.batch_number} ${batch.batch_type === 'custom' ? `(${batch.school_code})` : '(Reguler)'}`
            })
          }
        })
      }
    })

    // Filter by search term
    if (searchTerm) {
      return results.filter(r =>
        r.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.spkId.toLowerCase().includes(searchTerm.toLowerCase())
      )
    }

    return results
  }

  const handleEdit = (item) => {
    setEditingItem(item)
  }

  const handleEditSuccess = () => {
    setEditingItem(null)
    fetchData()
  }

  const stationConfig = STATION_TYPES.find(s => s.id === selectedStation)

  if (!selectedStation) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-xl font-bold text-gray-800">Edit Riwayat QC</h1>
        </div>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="text-blue-700 text-sm">
            Pilih stasiun untuk melihat dan mengedit data QC yang pernah diinput di stasiun tersebut.
            Data akan tetap bisa diedit meskipun buku sudah pindah ke stasiun berikutnya.
          </p>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {STATION_TYPES.map(station => {
            const Icon = station.icon
            const colorClasses = {
              amber: 'bg-amber-50 border-amber-200 hover:bg-amber-100',
              emerald: 'bg-emerald-50 border-emerald-200 hover:bg-emerald-100',
              cyan: 'bg-cyan-50 border-cyan-200 hover:bg-cyan-100',
              pink: 'bg-pink-50 border-pink-200 hover:bg-pink-100',
            }
            const iconColors = {
              amber: 'text-amber-600',
              emerald: 'text-emerald-600',
              cyan: 'text-cyan-600',
              pink: 'text-pink-600',
            }
            return (
              <button
                key={station.id}
                onClick={() => setSelectedStation(station.id)}
                className={`p-4 rounded-xl border-2 text-left transition-all ${colorClasses[station.color]}`}
              >
                <div className="flex items-center gap-3 mb-2">
                  <Icon className={`w-8 h-8 ${iconColors[station.color]}`} />
                  <span className="font-bold text-gray-800">{station.label}</span>
                </div>
                <p className="text-sm text-gray-600">{station.description}</p>
              </button>
            )
          })}
        </div>
      </div>
    )
  }

  const qcData = getQCDataForStation()
  const Icon = stationConfig?.icon || Layers

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSelectedStation(null)}
            className="p-2 hover:bg-gray-100 rounded-lg"
          >
            <X className="w-5 h-5" />
          </button>
          <Icon className={`w-6 h-6 text-${stationConfig?.color}-600`} />
          <h1 className="text-xl font-bold text-gray-800">{stationConfig?.label}</h1>
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
        <input
          type="text"
          value={searchTerm}
          onChange={e => setSearchTerm(e.target.value)}
          placeholder="Cari berdasarkan judul SPK atau kode..."
          className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm"
        />
      </div>

      {/* Info */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
        <p className="text-yellow-700 text-sm flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" />
          Menampilkan {qcData.length} data QC {stationConfig?.label} yang bisa diedit
        </p>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : qcData.length === 0 ? (
        <div className="text-center py-12 text-gray-500">
          <Icon className="w-12 h-12 mx-auto mb-3 opacity-50" />
          <p>Tidak ada data QC {stationConfig?.label}</p>
        </div>
      ) : (
        <div className="space-y-2">
          {qcData.map((item, idx) => (
            <QCHistoryCard
              key={`${item.spkId}-${item.batchId || item.schoolCode || 'main'}-${idx}`}
              item={item}
              stationConfig={stationConfig}
              onEdit={() => handleEdit(item)}
            />
          ))}
        </div>
      )}

      {/* Edit Modal */}
      {editingItem && (
        <EditQCModal
          item={editingItem}
          stationConfig={stationConfig}
          onClose={() => setEditingItem(null)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  )
}

// ============================================
// QC HISTORY CARD
// ============================================
function QCHistoryCard({ item, stationConfig, onEdit }) {
  const colorClasses = {
    amber: 'border-l-amber-500 bg-amber-50',
    emerald: 'border-l-emerald-500 bg-emerald-50',
    cyan: 'border-l-cyan-500 bg-cyan-50',
    pink: 'border-l-pink-500 bg-pink-50',
  }

  return (
    <div className={`rounded-lg border-l-4 p-3 ${colorClasses[stationConfig?.color] || 'border-l-gray-500 bg-gray-50'}`}>
      <div className="flex justify-between items-start">
        <div className="flex-1">
          <p className="font-medium text-gray-800 text-sm">{item.label}</p>
          <p className="text-xs text-gray-500">{item.spkId}</p>

          <div className="mt-2 flex flex-wrap gap-3 text-xs">
            <span className="bg-white px-2 py-1 rounded">
              Awal: <strong>{item.data.jumlahAwal}</strong>
            </span>
            {item.data.jumlahReject > 0 && (
              <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                Reject: <strong>{item.data.jumlahReject}</strong>
              </span>
            )}
            <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
              Akhir: <strong>{item.data.jumlahAkhir}</strong>
            </span>
            {item.data.operatorName && (
              <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded">
                Operator: {item.data.operatorName}
              </span>
            )}
          </div>

          {item.data.notes && (
            <p className="mt-1 text-xs text-gray-500 italic">Catatan: {item.data.notes}</p>
          )}
          {item.data.catatan && (
            <p className="mt-1 text-xs text-gray-500 italic">Catatan: {item.data.catatan}</p>
          )}
        </div>

        <button
          onClick={onEdit}
          className="flex items-center gap-1 px-3 py-1.5 bg-yellow-500 text-white rounded-lg text-xs hover:bg-yellow-600"
        >
          <Edit2 className="w-3 h-3" />
          Edit
        </button>
      </div>
    </div>
  )
}

// ============================================
// EDIT QC MODAL
// ============================================
function EditQCModal({ item, stationConfig, onClose, onSuccess }) {
  const [jumlahAwal, setJumlahAwal] = useState(item.data.jumlahAwal || 0)
  const [jumlahReject, setJumlahReject] = useState(item.data.jumlahReject || 0)
  const [notes, setNotes] = useState(item.data.notes || item.data.catatan || '')
  const [operatorName, setOperatorName] = useState(item.data.operatorName || '')
  const [saving, setSaving] = useState(false)

  const jumlahAkhir = jumlahAwal - jumlahReject

  const handleSave = async () => {
    setSaving(true)
    try {
      if (item.type === 'batch') {
        // Update batch QC
        await spkService.updateBatchQC(item.spkId, item.batchId, {
          qcType: item.qcType,
          jumlahAwal,
          jumlahReject,
          jumlahAkhir,
          notes,
          operatorName
        })
      } else {
        // Update SPK QC (laminasi)
        // Need to find the qc_results id first, or use a different endpoint
        // For now, we'll use the existing updateQCResult if we have the ID
        // or create a new endpoint for updating by type

        // Let's use a workaround - get qcResults from spk and find matching one
        const spkDetail = await spkService.getById(item.spkId)
        const qcResults = spkDetail.data.spk.qcResults || []
        const matchingQc = qcResults.find(q =>
          q.qc_type === item.qcType &&
          (item.schoolCode ? q.school_code === item.schoolCode : !q.school_code)
        )

        if (matchingQc) {
          await spkService.updateQCResult(item.spkId, matchingQc.id, {
            jumlahAwal,
            jumlahReject,
            jumlahAkhir,
            catatan: notes,
            operatorName
          })
        } else {
          throw new Error('Data QC tidak ditemukan di database')
        }
      }
      onSuccess()
    } catch (e) {
      alert('Error: ' + (e.response?.data?.message || e.message))
    }
    setSaving(false)
  }

  const colorClasses = {
    amber: 'bg-amber-500',
    emerald: 'bg-emerald-500',
    cyan: 'bg-cyan-500',
    pink: 'bg-pink-500',
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white w-full max-w-md mx-4 rounded-xl shadow-xl overflow-hidden">
        {/* Header */}
        <div className={`p-4 ${colorClasses[stationConfig?.color] || 'bg-gray-500'} text-white`}>
          <h3 className="font-bold text-lg">Edit {stationConfig?.label}</h3>
          <p className="text-sm opacity-90">{item.label}</p>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          {['binding', 'potong', 'laminasi'].includes(item.qcType) && (
            <div>
              <label className="text-sm font-medium text-gray-700">Nama Operator</label>
              <input
                type="text"
                value={operatorName}
                onChange={e => setOperatorName(e.target.value)}
                className="w-full border rounded p-2 mt-1 text-sm"
                placeholder="Masukkan nama operator"
              />
            </div>
          )}

          <div>
            <label className="text-sm font-medium text-gray-700">Jumlah Awal</label>
            <input
              type="number"
              value={jumlahAwal}
              onChange={e => setJumlahAwal(Number(e.target.value))}
              className="w-full border rounded p-2 mt-1 text-sm"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Jumlah Reject</label>
            <input
              type="number"
              value={jumlahReject}
              onChange={e => setJumlahReject(Number(e.target.value))}
              className="w-full border rounded p-2 mt-1 text-sm"
              min={0}
            />
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Jumlah Akhir</label>
            <div className="w-full border rounded p-2 mt-1 text-sm bg-green-50 text-green-700 font-bold">
              {jumlahAkhir}
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-gray-700">Catatan</label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              className="w-full border rounded p-2 mt-1 text-sm"
              rows={2}
              placeholder="Catatan tambahan"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 bg-gray-50 flex gap-2">
          <button
            onClick={onClose}
            className="flex-1 py-2.5 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-100"
          >
            Batal
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className={`flex-1 py-2.5 ${colorClasses[stationConfig?.color] || 'bg-blue-500'} text-white rounded-lg text-sm font-medium hover:opacity-90 flex items-center justify-center gap-2`}
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 animate-spin" />
                Menyimpan...
              </>
            ) : (
              <>
                <Check className="w-4 h-4" />
                Simpan
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}

export default EditQCHistoryPage
